import React, { useState } from 'react';
import { ICardProductInfos, useMediaQuery } from '@monofront/business/hooks';
import {
  createAddToShoppingCartInsight,
  createSubmitInsights,
} from '@services/productInsights';
import { productService } from '@services/businessContainer';
import useProductToShoppingCart from '@hooks/cart/useProductToShoppingCart';
import { AddToShoppingCartContext, ISearchProduct } from '@monofront/types';
import { sendMessageToMobile } from '@utils/mobile';
import { stopEventPropagation } from '@utils/stopEventPropagation';
import {
  ProductCardSlim,
  ProductBadges,
  ProductImage,
  ProductPrice,
  ProductAddToCart,
  ProductFormatAndUnitPrice,
  ProductTitle,
  ProductVendor,
  ProductPictograms,
  ProductBundle,
  ProductPromotionalOffer,
  ProductReviews,
  ProductFormat,
  HiddenPrice,
} from '@monofront/design-system/components/ProductCard';
import ButtonAddToWishlist from '@components/ButtonAddToWishlist';
import router from 'next/router';
import ProductDrawer from '@components/ProductDrawer';
import { getHasHiddenPrice } from '@monofront/business/utils';
import { useAuthentication } from '@context/businessContainer';
import { isMobileApp } from '@utils/environment';
import { KeyTextField } from '@prismicio/client';
import { useCardProductInfos } from '@hooks/businessContainer';
import {
  useEditorialSidePanelContext,
  useFeatureIsOn,
} from '@monofront/business/contexts';
import { ABTEST_STROKE_PRICE } from '@monofront/business/constants';

interface ICarouselProductCardProps {
  product: ISearchProduct;
  containerContext: AddToShoppingCartContext;
  isPurchasable: boolean;
  trackingId?: string | KeyTextField;
  onCardClick?: (product: Partial<ICardProductInfos>) => void;
  isOnCardClickDisabled?: boolean;
  isProductDrawerEnabled?: boolean;
  trackProductInteraction?: () => void;
}

const CarouselProductCard: React.FC<ICarouselProductCardProps> = ({
  product,
  containerContext,
  isPurchasable,
  trackingId,
  onCardClick,
  isOnCardClickDisabled,
  isProductDrawerEnabled,
  trackProductInteraction,
}) => {
  const isMobile = useMediaQuery(800);
  const submitInsights = createSubmitInsights({
    productPageIndex: productService.getProductPageIndex(),
    objectID: product.objectID,
  });
  const addToShoppingCartInsight = createAddToShoppingCartInsight({
    productPageIndex: productService.getProductPageIndex(),
    objectID: product.objectID,
  });
  const {
    localProductQuantity,
    currentProductQuantity,
    incrementCount,
    decrementCount,
  } = useProductToShoppingCart(
    product,
    containerContext,
    addToShoppingCartInsight,
  );
  const [selectedProduct, setSelectedProduct] = useState<
    ISearchProduct | undefined
  >(undefined);
  const { isAuthenticated } = useAuthentication();
  const shouldHidePrice = Boolean(
    !isAuthenticated && getHasHiddenPrice(product),
  );
  const { isAntiWaste } = useCardProductInfos(product);
  const { setEditorialPanelSourceUid, setEditorialSidePanelProps } =
    useEditorialSidePanelContext();
  const isABtestEnabled = useFeatureIsOn(ABTEST_STROKE_PRICE);

  const handleClickItem = (event: React.SyntheticEvent) => {
    onCardClick?.(product);
    trackProductInteraction?.();

    if (isMobile && isProductDrawerEnabled) {
      setSelectedProduct(
        selectedProduct?.id === product.id ? undefined : product,
      );
    }

    if (isOnCardClickDisabled) {
      stopEventPropagation(event);

      return;
    }

    submitInsights();

    if (isMobileApp) {
      sendMessageToMobile('goToProduct', { handle: product.handle });
      stopEventPropagation(event);

      return;
    }

    if (trackingId) {
      router.push(`/products/${product.handle}?trackingId=${trackingId}`);
      stopEventPropagation(event);
    }
  };

  const handleAddToCart = () => {
    incrementCount();
    trackProductInteraction?.();
  };

  const onTooltipClick = isABtestEnabled
    ? () => {
        setEditorialPanelSourceUid(
          isAntiWaste ? 'stroke-price-anti-waste' : 'stroke-price',
        );
        setEditorialSidePanelProps({ product });
      }
    : undefined;

  return (
    <>
      <ProductCardSlim product={product} isInCart={currentProductQuantity > 0}>
        <ProductImage />
        <ProductPictograms />
        <ProductBadges />
        {shouldHidePrice ? (
          <HiddenPrice />
        ) : (
          <ProductPrice
            onTooltipClick={onTooltipClick}
            quantity={localProductQuantity ?? 0}
          />
        )}
        <ProductBundle />
        <ProductPromotionalOffer quantity={localProductQuantity ?? 0} />
        {shouldHidePrice ? (
          <ProductFormat />
        ) : (
          <ProductFormatAndUnitPrice quantity={localProductQuantity ?? 0} />
        )}
        <ProductTitle handleClick={handleClickItem} />
        <ButtonAddToWishlist product={product} />
        {isPurchasable && (
          <ProductAddToCart
            handleAddToCart={handleAddToCart}
            handleRemoveFromCart={decrementCount}
            quantity={localProductQuantity ?? 0}
          />
        )}
        <ProductVendor />
        <ProductReviews />
      </ProductCardSlim>
      {isProductDrawerEnabled && (
        <ProductDrawer
          isOpen={!!selectedProduct}
          externalToggle={() => setSelectedProduct(undefined)}
          productHandle={product.handle}
          product={product}
        />
      )}
    </>
  );
};

export default CarouselProductCard;
